import { Stack, Typography } from '@mui/material';
import { ScheduleRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTenantTimeFormat } from '../../../stores/tenant.store';
import { wallTime } from '../util/classroom';

// ─── El horario de una sección, como lo lee el alumno (g4) ──────────────────
// «Jue 7:00 PM–9:00 PM · Sala 1», en el formato de hora del seminario. Un
// bloque importado con término = inicio dice solo la hora de inicio
// («Jue 7:00 PM · Site 9»), igual que CourseCoreFacts: no se inventa un
// término. Sin horario: «Horario por confirmar».

/** Claves literales (el guardián de i18n busca cada clave escrita entera).
 *  Índice = dayOfWeek (0 = domingo). */
const SHORT_DAY_KEYS = [
  'portal.calendar.daySun',
  'portal.calendar.dayMon',
  'portal.calendar.dayTue',
  'portal.calendar.dayWed',
  'portal.calendar.dayThu',
  'portal.calendar.dayFri',
  'portal.calendar.daySat',
] as const;

export interface ScheduleBlock {
  dayOfWeek: number;
  startTime: string;
  endTime?: string | null;
  location?: string;
}

/** Las horas de un bloque o de una clase: «7:00 PM–9:00 PM» o «7:00 PM». */
export function useTimeRange(): (start: string, end?: string | null) => string {
  const fmt = useTenantTimeFormat();
  return (start, end) =>
    end && end > start ? `${wallTime(start, fmt)}–${wallTime(end, fmt)}` : wallTime(start, fmt);
}

/** «Jue 7:00 PM · Site 9». */
export function useScheduleBlockText(): (b: ScheduleBlock) => string {
  const { t } = useTranslation('sis');
  const range = useTimeRange();
  return (b) => {
    const day = t(SHORT_DAY_KEYS[b.dayOfWeek] ?? SHORT_DAY_KEYS[0]);
    const text = t('portal.schedule.block', { day, time: range(b.startTime, b.endTime) });
    return b.location ? `${text} · ${b.location}` : text;
  };
}

/** La línea de horario de la tarjeta de un curso. */
export function SectionScheduleLine({
  schedule,
}: {
  schedule: readonly ScheduleBlock[] | undefined | null;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const blockText = useScheduleBlockText();
  const blocks = (schedule ?? []).filter((b) => /^\d{2}:\d{2}$/.test(b.startTime));
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      <ScheduleRounded sx={{ fontSize: 14, color: '#8b949e' }} />
      <Typography variant="caption" color={blocks.length ? '#c9d1d9' : '#8b949e'} noWrap>
        {blocks.length
          ? blocks.map((b) => blockText(b)).join(' / ')
          : t('portal.schedule.toBeConfirmed')}
      </Typography>
    </Stack>
  );
}
